import { ShortCode } from '@highereducation/wordpress';
import { InfoCard, type InfoCardProps } from '@highereducation/hen-ui';

type InfoCardCtaProps = NonNullable<InfoCardProps['cta']>;

type InfoCardShortcodeProps = {
  'img-src'?: string;
  'img-alt'?: string;
  icon?: InfoCardProps['icon'];
  'icon-color'?: InfoCardProps['iconColor'];
  'body-color'?: InfoCardProps['bodyColor'];
  brow?: string;
  title?: string;
  variant?: InfoCardProps['variant'];
  size?: InfoCardProps['size'];
  align?: InfoCardProps['align'];
  'cta-label'?: string;
  'cta-href'?: string;
  'cta-variant'?: InfoCardCtaProps['variant'];
  'cta-icon-after'?: InfoCardCtaProps['iconAfter'];
  children?: React.ReactNode;
};

/**
 * InfoCardShortcode - Shortcode wrapper for InfoCard composite component
 *
 * Handles prop conversion from WordPress shortcode attributes (strings) to
 * component props (typed values). This shortcode provides an information card
 * component with optional image, title, description, and call-to-action.
 *
 * **Note:** className is intentionally excluded from shortcode props as per design system guidelines.
 *
 * Supported shortcode attributes:
 * - img-src: string (optional) - Image source URL
 * - img-alt: string (optional) - Alt text for the image (defaults to "{title} icon" if title provided)
 * - icon: string (optional) - Lucide icon name rendered above the content group (e.g. "telescope"). Takes precedence over img-src.
 * - icon-color: string (optional) - Icon color token (defaults to "heading")
 * - body-color: string (optional) - Body copy color token (e.g. "subtext"). Defaults to "body".
 * - brow: string (optional) - Uppercase contextual label rendered above the title
 * - title: string (optional) - Card title text
 * - variant: 'background-white' | 'background-gray' | 'background-transparent' (optional) - Inner Card background
 * - size: 'default' | 'lg' (optional) - Padding/gap scale (lg matches the Figma "Info Card" large format)
 * - align: 'start' | 'center' (optional) - Content alignment (center matches the "Our Why" cards)
 * - cta-label: string (optional) - Call-to-action label (requires cta-href)
 * - cta-href: string (optional) - Call-to-action destination URL (requires cta-label)
 * - cta-variant: string (optional) - Button variant for the CTA (defaults to textOnly)
 * - cta-icon-after: string (optional) - Trailing Lucide icon name for the CTA (e.g. arrow-right)
 *
 * @example WordPress shortcode usage:
 * [hen-ui-info-card
 *     img-src="/path/to/image.png"
 *     img-alt="Card image"
 *     title="Card Title"
 *     variant="background-gray"
 *     size="lg"
 *     cta-label="Learn more"
 *     cta-href="/learn"
 *     cta-icon-after="arrow-right"
 * ]
 * This is the description text.
 * [/hen-ui-info-card]
 */
export const InfoCardShortcode: ShortCode<InfoCardShortcodeProps> = {
  component: ({
    'img-src': imageSrc,
    'img-alt': imageAlt,
    icon,
    'icon-color': iconColor,
    'body-color': bodyColor,
    brow,
    title,
    variant,
    size,
    align,
    'cta-label': ctaLabel,
    'cta-href': ctaHref,
    'cta-variant': ctaVariant,
    'cta-icon-after': ctaIconAfter,
    children,
  }) => {
    const cta: InfoCardProps['cta'] =
      ctaLabel && ctaHref
        ? {
            label: ctaLabel,
            href: ctaHref,
            variant: ctaVariant,
            iconAfter: ctaIconAfter,
          }
        : undefined;

    return (
      <InfoCard
        imageSrc={imageSrc}
        imageAlt={imageAlt}
        icon={icon}
        iconColor={iconColor}
        bodyColor={bodyColor}
        brow={brow}
        title={title}
        variant={variant}
        size={size}
        align={align}
        cta={cta}
      >
        {children}
      </InfoCard>
    );
  },
};
